import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CloudUpload, RefreshCw, Smartphone, XCircle } from "lucide-react";
import { dismissItem, subscribeQueue, syncNow } from "../lib/offlineSync";
import { itDate, itDateTime } from "./SafeGuardArchive";
import { Card } from "./ui-kit";

const GROUPS = [
  { key: "pending", statuses: ["in_attesa", "in_invio"], title: "Solo su questo dispositivo — in attesa di invio", icon: CloudUpload, tone: "text-blue-700 bg-blue-50",
    note: "Non ancora presenti sul server né nei report archiviati. Verranno inviate automaticamente." },
  { key: "conflict", statuses: ["conflitto"], title: "In conflitto con un altro dispositivo", icon: AlertTriangle, tone: "text-red-700 bg-red-50",
    note: "Il controllo era già registrato: quella esistente non è stata sostituita. Questa versione è conservata sul server e il consulente la vede." },
  { key: "rejected", statuses: ["rifiutata"], title: "Non accettate dal server", icon: XCircle, tone: "text-red-700 bg-red-50",
    note: "Leggi il motivo e ripeti la registrazione nell'app, oppure contatta il consulente." },
  { key: "synced", statuses: ["sincronizzata"], title: "Sincronizzate (confermate dal server)", icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50",
    note: "Salvate sul server: compariranno nel report del giorno indicato." },
];

// Elenco delle registrazioni fatte su questo dispositivo. Funziona anche senza login (pagina offline):
// i dati vengono solo da IndexedDB.
export default function DeviceRecords({ canSync = true, tenantId }) {
  const [q, setQ] = useState(null);
  useEffect(() => subscribeQueue(setQ, tenantId), [tenantId]);
  const hasItems = !!q?.items.length;
  useEffect(() => {
    if (hasItems && window.location.hash === "#dispositivo") document.getElementById("dispositivo")?.scrollIntoView({ behavior: "smooth" });
  }, [hasItems]);
  if (!q || !q.items.length) return null;
  const remove = async (item) => {
    if (!window.confirm("Togliere questa registrazione dall'elenco del dispositivo? Se è in conflitto resta comunque conservata sul server.")) return;
    await dismissItem(item.client_record_id);
  };
  return (
    <Card id="dispositivo" className="scroll-mt-24 p-5 sm:p-6" data-testid="device-records">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-1 h-5 w-5 text-gold" strokeWidth={1.5} />
          <div>
            <h3 className="font-heading text-xl font-semibold">Registrazioni su questo dispositivo</h3>
            <p className="text-sm text-slate-500">Fatte da qui, anche senza connessione. Distinte dai documenti già archiviati.</p>
          </div>
        </div>
        {canSync && q.pending > 0 && navigator.onLine && (
          <button type="button" onClick={() => syncNow()} disabled={q.syncing} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-navy px-4 text-sm font-medium text-navy hover:bg-slate-50 disabled:opacity-60" data-testid="device-sync-now">
            <RefreshCw className={`h-4 w-4 ${q.syncing ? "animate-spin" : ""}`} />{q.syncing ? "Invio in corso…" : "Invia ora"}
          </button>
        )}
      </div>
      <div className="mt-4 space-y-5">
        {GROUPS.map((g) => {
          const items = q.items.filter((i) => g.statuses.includes(i.status)).reverse();
          if (!items.length) return null;
          return (
            <div key={g.key} data-testid={`device-group-${g.key}`}>
              <p className="flex items-center gap-2 text-sm font-semibold text-navy"><span className={`grid h-7 w-7 place-items-center rounded-lg ${g.tone}`}><g.icon className="h-4 w-4" /></span>{g.title} ({items.length})</p>
              <p className="ml-9 text-xs text-slate-500">{g.note}</p>
              <ul className="ml-9 mt-2 divide-y divide-hairline rounded-lg border border-hairline">
                {items.map((item) => (
                  <li key={item.client_record_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                    <span className="w-24 shrink-0 font-medium text-navy">{itDate(item.body.reference_date)}</span>
                    <span className="min-w-0 flex-1">
                      {item.procedure_name || "Controllo HACCP"}
                      <span className="block text-xs text-slate-500">
                        {item.user_name || "Operatore"} · compilata {itDateTime(item.body.device_time || item.created_at)}
                        {item.synced_at ? ` · ricevuta dal server ${itDateTime(item.server_time || item.synced_at)}` : ""}
                        {item.status === "in_invio" ? " · invio in corso" : ""}
                        {OPEN_ERR(item)}
                      </span>
                      {item.last_error && !["sincronizzata"].includes(item.status) && <span className={`block text-xs ${["conflitto", "rifiutata"].includes(item.status) ? "text-red-700" : "text-slate-500"}`}>{item.last_error}</span>}
                    </span>
                    {["conflitto", "rifiutata"].includes(item.status) && (
                      <button type="button" onClick={() => remove(item)} className="min-h-[36px] text-xs font-medium text-slate-500 underline" data-testid={`device-dismiss-${item.client_record_id}`}>Ho preso nota, togli dall'elenco</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const OPEN_ERR = (item) => (item.status === "in_attesa" && item.attempts ? ` · tentativi ${item.attempts}` : "");
